import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Textarea } from '@artosera/ui';
import type { BrouillonRegistrePolice } from '@artosera/shared';
import { galerieALaction } from '../../auth/sujet.js';
import {
  registrePoliceApi,
  useMutationRegistre,
  usePropositionRegistre,
} from '../../api/registrePolice.js';
import { estApiError } from '../../api/errors.js';
import { useShellContext } from '../shell/context.js';
import { Field } from '../Field.js';
import { MentionsForm } from './MentionsForm.js';
import styles from './RegistrePolice.module.css';

export function PropositionPage() {
  const { t } = useTranslation(['regie', 'commun']);
  const { galerieSlug, galerie } = useShellContext();
  const { id } = useParams();
  const naviguer = useNavigate();
  const peutLire = galerieALaction(galerie, 'registre-police:read');
  const peutInscrire = galerieALaction(galerie, 'registre-police:inscrire');
  const voitPiece = galerieALaction(galerie, 'registre-police:rectifier');
  const nouvelle = !id;
  const proposition = usePropositionRegistre(galerieSlug, id, peutLire);
  const [confirmation, setConfirmation] = useState<string | null>(null);
  const [motif, setMotif] = useState('');
  const [ecartOuvert, setEcartOuvert] = useState(false);
  const enregistrer = useMutationRegistre(
    galerieSlug,
    (brouillon: BrouillonRegistrePolice, appel) =>
      id
        ? registrePoliceApi.completer(galerieSlug, id, brouillon, appel)
        : registrePoliceApi.creer(galerieSlug, brouillon, appel),
  );
  const inscrire = useMutationRegistre(galerieSlug, (propositionId: string, appel) =>
    registrePoliceApi.inscrire(galerieSlug, propositionId, appel),
  );
  const ecarter = useMutationRegistre(galerieSlug, (raison: string, appel) =>
    registrePoliceApi.ecarter(galerieSlug, id!, raison, appel),
  );
  const label = (cle: string) => t(`regie:registrePolice.${cle}`);
  const erreur = enregistrer.error || inscrire.error || ecarter.error || proposition.error;
  async function soumettre(brouillon: BrouillonRegistrePolice, _raison: string, valider: boolean) {
    try {
      const reponse = await enregistrer.mutateAsync(brouillon);
      if (valider) setConfirmation(reponse.proposition.id);
      else if (nouvelle)
        void naviguer(`/${galerieSlug}/registre-police/propositions/${reponse.proposition.id}`);
    } catch {
      /* Le formulaire garde les mentions si l’enregistrement échoue. */
    }
  }
  return (
    <section className={styles.page} data-ui="page">
      <header>
        <Link to={`/${galerieSlug}/registre-police`}>{label('titre')}</Link>
        <h1>{label(nouvelle ? 'inscrireObjet' : 'propositionTitre')}</h1>
      </header>
      {!peutLire && <p role="alert">{label('refus')}</p>}
      {peutLire && !nouvelle && proposition.isPending && <p>{t('commun:chargement')}</p>}
      {erreur && (
        <p role="alert">{estApiError(erreur) ? erreur.message : t('commun:erreurReseau')}</p>
      )}
      {peutLire && proposition.data?.proposition.etat === 'inscrite' && (
        <Link
          to={`/${galerieSlug}/registre-police/${proposition.data.proposition.inscriptionNumero}`}
        >
          {label('voirInscription')}
        </Link>
      )}
      {peutLire && proposition.data?.proposition.etat === 'ecartee' && (
        <p>{proposition.data.proposition.motifEcart}</p>
      )}
      {peutLire && (nouvelle || proposition.data?.proposition.etat === 'en_attente') && (
        <>
          {proposition.data && (
            <p>
              {t('regie:registrePolice.manquantes', {
                count: proposition.data.proposition.mentionsManquantes.length,
              })}
            </p>
          )}
          {peutInscrire ? (
            <MentionsForm
              key={id ?? 'nouvelle'}
              initial={proposition.data?.proposition.brouillon ?? {}}
              galerieSlug={galerieSlug}
              voitPiece={voitPiece}
              enCours={enregistrer.isPending || inscrire.isPending || ecarter.isPending}
              peutValider={!nouvelle}
              onEnregistrer={soumettre}
            />
          ) : (
            <p>{label('refusInscrire')}</p>
          )}
          {peutInscrire && !nouvelle && (
            <Button onClick={() => setEcartOuvert(true)}>{label('ecarter')}</Button>
          )}
        </>
      )}
      <Dialog
        open={confirmation !== null && peutInscrire}
        onOpenChange={(ouvert) => {
          if (!ouvert) setConfirmation(null);
        }}
        title={label('valider')}
        closeLabel={t('commun:actions.fermer')}
      >
        <p>{label('confirmationDefinitive')}</p>
        <Button
          disabled={inscrire.isPending}
          onClick={() => {
            if (!confirmation) return;
            void inscrire
              .mutateAsync(confirmation)
              .then((reponse) => {
                setConfirmation(null);
                void naviguer(`/${galerieSlug}/registre-police/${reponse.inscription.numero}`);
              })
              .catch(() => setConfirmation(null));
          }}
        >
          {label('confirmer')}
        </Button>
      </Dialog>
      <Dialog
        open={ecartOuvert && peutInscrire}
        onOpenChange={setEcartOuvert}
        title={label('ecarter')}
        closeLabel={t('commun:actions.fermer')}
      >
        <form
          className={styles.formulaire}
          onSubmit={(event) => {
            event.preventDefault();
            void ecarter
              .mutateAsync(motif.trim())
              .then(() => void naviguer(`/${galerieSlug}/registre-police`))
              .catch(() => setEcartOuvert(false));
          }}
        >
          <Field htmlFor="motif-ecart" label={label('motif')}>
            <Textarea
              id="motif-ecart"
              required
              value={motif}
              onChange={(event) => setMotif(event.target.value)}
            />
          </Field>
          <Button type="submit" disabled={!motif.trim() || ecarter.isPending}>
            {label('ecarter')}
          </Button>
        </form>
      </Dialog>
    </section>
  );
}
